import {
  AgxButton,
  AgxDatePicker,
  AgxDropdown,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Icon_AlertCircleOutline,
  Icon_FeaturedIcon,
} from '@urbanx/agx.ui-components2';
import { AgxFileUpload } from '@urbanx/agx-ui-components';
import { AutopilotTaskDto } from '@urbanx/agx.activities.client';
import {
  DocumentTypes,
  StaffGetContractSettlementResponseSettlementType as SettlementType,
  UploadedDocument,
} from '@urbanx/agx.campaigns.client';
import { useSelectedCampaign } from 'hooks';
import { deleteFile, getFileLink } from 'services';
import { useMarkedAsSettled } from './useMarkedAsSettled';
import './MarkedAsSettled.scss';

interface Props {
  isOpen: boolean;
  setIsOpen: (_value: boolean) => void;
  task: AutopilotTaskDto;
  onTasksInvalidated?: () => void | Promise<void>;
}

const settlementTypeOptions = [
  { label: 'PEXA', value: SettlementType.Pexa },
  { label: 'In person', value: SettlementType.InPerson },
];

const getSettlementTaskId = (task: AutopilotTaskDto) => {
  if (!task.id) throw new Error('Settlement task ID is required');
  return task.id;
};

const MarkedAsSettled = ({ task, isOpen, setIsOpen, onTasksInvalidated }: Props) => {
  const campaignId = task.campaignId;
  const contractId = task.contractId;
  const {
    getAuthToken,
    onUploadFile,
    settlementDate,
    setSettlementDate,
    settlementType,
    setSettlementType,
    vendorConfirmationFiles,
    setVendorConfirmationFiles,
    buyerConfirmationFiles,
    setBuyerConfirmationFiles,
    pexaConfirmationFiles,
    setPexaConfirmationFiles,
    isSubmitting,
    isAwaitingTaskReconciliation,
    canComplete,
    clearSelection,
    onSaveAndExit,
    onComplete,
  } = useMarkedAsSettled({
    taskId: getSettlementTaskId(task),
    contractId,
    campaignId,
    isOpen,
    dialogOpenChangeHandler: setIsOpen,
    onTasksInvalidated,
  });
  const { campaign: selectedCampaign } = useSelectedCampaign();
  const uploadCampaignId = campaignId || selectedCampaign?.campaignId || '';
  const handleDialogOpenChange = (isDialogOpen: boolean) => {
    if (!isDialogOpen) clearSelection();
    setIsOpen(isDialogOpen);
  };
  const fileUpload = (
    id: string,
    title: string,
    documentType: DocumentTypes,
    fileData: (UploadedDocument | File)[],
    setFileData: (files: (UploadedDocument | File)[]) => void
  ) => (
    <AgxFileUpload
      id={id}
      title={title}
      documentType={documentType}
      buttonText="Choose file"
      fileData={fileData}
      onValueChanged={({ value }) => setFileData(value ?? [])}
      getAuthToken={getAuthToken}
      campaignId={uploadCampaignId}
      uploadFile={onUploadFile}
      deleteFile={deleteFile}
      getFileLink={getFileLink}
      extensions={['pdf']}
      multiple={false}
      autoUpload={false}
      required
    />
  );

  return (
    <AgxModal
      id="mark-as-settled-modal"
      type="single"
      secondaryTitle={
        <AgxSpacer direction="vertical" gap="sm">
          <Icon_FeaturedIcon
            icon={<Icon_AlertCircleOutline type="Danger" />}
            color="var(--text-error)"
            backgroundColor="var(--bg-error-light)"
          />
          <AgxTypography id="title" type="body_ui_strong" size="medium">
            Mark as Settled
          </AgxTypography>
        </AgxSpacer>
      }
      actionButtons={[
        <AgxButton
          key="btnSaveExit"
          id="btnSaveExit"
          text="Save & Exit"
          variant="hollow"
          size="large"
          onClick={onSaveAndExit}
          disabled={isSubmitting || isAwaitingTaskReconciliation}
          stretch
        />,
        <AgxButton
          key="btnConfirm"
          id="btnConfirm"
          text={isAwaitingTaskReconciliation ? 'Retry task update' : 'Mark as Sold'}
          variant="primary"
          size="large"
          disabled={isSubmitting || !canComplete}
          onClick={onComplete}
          stretch
        />,
      ]}
      variant="withDivider"
      actionButtonContainerStyle="withoutDivider"
      setIsDialogOpen={handleDialogOpenChange}
      isOpen={isOpen}
      minWidth="600px"
      maxWidth="600px"
      content={
        <AgxSpacer
          direction="vertical"
          gap="md"
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          <AgxDatePicker
            id="settlementDate"
            label="Settlement Date"
            required
            optionalText=""
            value={settlementDate}
            onChange={setSettlementDate}
          />
          <AgxDropdown
            id="settlementType"
            label="Settlement Type"
            options={settlementTypeOptions}
            selected={settlementType}
            onChange={(value) => setSettlementType(value as SettlementType)}
            hideOptionalText
            required
          />
          {settlementType === SettlementType.Pexa ? (
            fileUpload(
              'fileUploadPexa',
              'PEXA Confirmation',
              DocumentTypes.PexaSettlementConfirmation,
              pexaConfirmationFiles,
              setPexaConfirmationFiles
            )
          ) : (
            <>
              {fileUpload(
                'fileUploadVendor',
                'Vendor Confirmation',
                DocumentTypes.VendorSettlementConfirmation,
                vendorConfirmationFiles,
                setVendorConfirmationFiles
              )}
              {fileUpload(
                'fileUploadBuyer',
                'Buyer Confirmation',
                DocumentTypes.BuyerSettlementConfirmation,
                buyerConfirmationFiles,
                setBuyerConfirmationFiles
              )}
            </>
          )}
        </AgxSpacer>
      }
    />
  );
};

export default MarkedAsSettled;
